import React, { useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { myMutation } from "../../hooks/myMutation";
import { FieldRenderer } from "./FieldRenderer";
import { StepIndicator2 } from "./StepIndicator2";
import { StepIndicator } from "./StepIndicator";

export interface Step {
  id: string;
  label: string;
  icon?: React.ReactNode;
  fields: any[];
  onEnterApi?: {
    endpoint: string;
    method?: "POST" | "PUT" | "PATCH";
    saveResponseTo: string;
  };
  skipIf?: {
    when: (values: any) => boolean;
  };
}

interface MultiStepFormSchema {
  steps: Step[];
}

export function MyMultiStepFormBuilder({
  schema,
  onFinalSubmit,
  onDataChange,
  onFormReady,
}: {
  schema: MultiStepFormSchema;
  onFinalSubmit: (data: any) => void;
  onDataChange: (data: any) => void;
  onFormReady: (data: any) => void;
}) {

  const [stepIndex, setStepIndex] = useState(0);
  const firedStepsRef = useRef<Set<string>>(new Set());

  const methods = useForm({
    mode: "onChange",
    shouldUnregister: false,
  });

  useEffect(() => {
    onFormReady?.(methods);
  }, []);

  const { control } = methods;
  const allValues = useWatch({ control });

  useEffect(() => {
    onDataChange?.(allValues);
  }, [allValues, onDataChange]);

  const currentStep = schema.steps[stepIndex];
  if (!currentStep) return null;

  /* ================= Helpers ================= */

  const shouldSkipStep = (step: Step) =>
    step.skipIf?.when(methods.getValues()) === true;

  const getNextStepIndex = (from: number) => {
    let i = from + 1;
    while (i < schema.steps.length && shouldSkipStep(schema.steps[i])) i++;
    return i;
  };

  const getPrevStepIndex = (from: number) => {
    let i = from - 1;
    while (i >= 0 && shouldSkipStep(schema.steps[i])) i--;
    return Math.max(i, 0);
  };

  /* ================= Step API ================= */

  const stepApi = myMutation<any, any>({
    endpoint: currentStep.onEnterApi?.endpoint ?? "",
    method: currentStep.onEnterApi?.method ?? "POST",
    enabled: !!currentStep.onEnterApi,

    onSuccess: (data) => {
      if (!currentStep.onEnterApi) return;

      const savedValue = extractSavedValue(
        data,
        currentStep.onEnterApi.saveResponseTo
      );

      if (savedValue === undefined) {
        console.warn(
          "Step API did not return a value to save",
          currentStep.onEnterApi.saveResponseTo,
          data
        );
        return;
      }

      methods.setValue(
        currentStep.onEnterApi.saveResponseTo,
        savedValue,
        { shouldDirty: true }
      );

      firedStepsRef.current.add(currentStep.id);
      setStepIndex(getNextStepIndex(stepIndex));
    },
  });

  /* ================= Navigation ================= */

// Replace the nextStep function in MyMultiStepFormBuilder with this:

async function nextStep() {
  // Get all field names from current step that are visible
  const visibleFields = currentStep.fields.filter(field => {
    // Check if field is visible based on its visibleWhen condition
    if (!field.visibleWhen) return true;
    // You might need to evaluate visibility here
    return true;
  });
  
  const fieldNames = visibleFields.map((f) => f.name);
  
  // Only validate if there are fields to validate
  let isValid = true;
  if (fieldNames.length > 0) {
    isValid = await methods.trigger(fieldNames);
  }
  
  if (!isValid) {
    // Scroll to first error
    const firstError = document.querySelector('.field-error');
    if (firstError) {
      firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    return;
  }

  // Check if we need to call API for this step
  if (
    currentStep.onEnterApi &&
    !firedStepsRef.current.has(currentStep.id)
  ) {
    const currentValues = methods.getValues();
    stepApi.mutate(currentValues);
    return;
  }

  // Get next step index
  const next = getNextStepIndex(stepIndex);
  
  // If this was the last step, submit the form
  if (next >= schema.steps.length) {
    const allValues = methods.getValues();
    const isFinalValid = await methods.trigger();
    if (isFinalValid) {
      onFinalSubmit(allValues);
    }
    return;
  }

  // Move to next step
  setStepIndex(next);
}

  function prevStep() {
    setStepIndex(getPrevStepIndex(stepIndex));
  }

  /* ================= Initial Step ================= */

  useEffect(() => {
    const firstValid = schema.steps.findIndex(
      (s) => !shouldSkipStep(s)
    );
    setStepIndex(firstValid === -1 ? 0 : firstValid);
  }, []);

  /* ================= Render ================= */

  return (
    <FormProvider {...methods}>
      <div className="w-full min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 py-6 px-3 sm:py-8 sm:px-4 md:py-10">
        {/* Step indicator */}
        <StepIndicator steps={schema.steps} currentStep={schema.steps[stepIndex]} />

        {/* Card */}
        <form
          key={currentStep.id}
          onSubmit={(e) => {
            e.preventDefault();
            nextStep();
          }}
          className="
            w-full max-w-3xl
            mx-auto
           
            space-y-5 sm:space-y-6
            transition-all duration-300
          "
        >
          {/* Step Header with Icon */}
          <div className="flex items-center gap-3 pb-3 border-b border-gray-200 dark:border-gray-700">
            {currentStep.icon && (
              <div className="text-blue-500 dark:text-blue-400">
                {currentStep.icon}
              </div>
            )}
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800 dark:text-white">
              {currentStep.label}
            </h2>
            <div className="ml-auto text-sm text-gray-500 dark:text-gray-400">
              Step {stepIndex + 1} of {schema.steps.length}
            </div>
          </div>

          {/* Fields Grid - Responsive */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
            {currentStep.fields.map((field) => (
              <div 
                key={field.name} 
                className={`
                  ${field.fullWidth ? 'sm:col-span-2' : 'col-span-1'}
                  transition-all duration-200
                `}
              >
                <FieldRenderer field={field} />
              </div>
            ))}
          </div>

          {/* Actions Buttons */}
          <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4 sm:pt-6">
            {stepIndex > 0 && (
              <button
                type="button"
                onClick={prevStep}
                disabled={stepApi.isPending}
                className="
                  w-full sm:flex-1
                  py-2.5 sm:py-3 px-4
                  rounded-xl
                  border-2 border-gray-300 dark:border-gray-600
                  bg-white dark:bg-gray-700
                  text-gray-700 dark:text-gray-200
                  font-medium
                  hover:bg-gray-50 dark:hover:bg-gray-600
                  active:scale-95
                  transition-all duration-200
                  disabled:opacity-50 disabled:cursor-not-allowed
                "
              >
                ← Back
              </button>
            )}

            <button
              type="button"
              onClick={nextStep}
              disabled={stepApi.isPending}
              className="
                w-full sm:flex-1
                py-2.5 sm:py-3 px-4
                rounded-xl
                bg-gradient-to-r from-blue-600 to-blue-700
                hover:from-blue-700 hover:to-blue-800
                text-white font-semibold
                shadow-md hover:shadow-lg
                active:scale-95
                transition-all duration-200
                disabled:opacity-50 disabled:cursor-not-allowed
                flex items-center justify-center gap-2
              "
            >
              {stepApi.isPending ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </>
              ) : (
                <>
                  {stepIndex === schema.steps.length - 1 ? (
                    "Submit ✓"
                  ) : (
                    "Next →"
                  )}
                </>
              )}
            </button>
          </div>

          {/* Progress Bar */}
          <div className="pt-2">
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
              <div 
                className="bg-gradient-to-r from-blue-500 to-blue-600 h-1.5 rounded-full transition-all duration-500"
                style={{ width: `${((stepIndex + 1) / schema.steps.length) * 100}%` }}
              />
            </div>
          </div>
        </form>
      </div>
    </FormProvider>
  );
}



export function extractSavedValue(data: any, key?: string) {
  if (data?.id != null) return data.id;
  if (Array.isArray(data?.ids) && data.ids.length) return data.ids[0];
  if (key) return data?.[key] ?? data?.data?.[key];
  return undefined;
}